import { supabase } from '@/lib/supabase/client'
import type { CatalogItem } from '@hangarmax/types'
import { fetchAllPages, type CatalogProvenance } from '@/lib/dimensionProvenance'

// Range-paginated: PostgREST clamps every response to max_rows (1 000), and the
// catalog has ~2 560 rows — a bare select returned only the first 1 000 (all
// aircraft, no vehicles/equipment). `id` is the tiebreaker so page boundaries are
// deterministic (`sort_order` is not unique). Same pattern as the web app's
// fetchCatalogFromSupabase().
export async function fetchAllCatalogItems(): Promise<CatalogItem[]> {
  return fetchAllPages(async (from, to) => {
    const { data, error } = await supabase
      .from('asset_catalog')
      .select('*')
      .order('category')
      .order('sort_order')
      .order('id')
      .range(from, to)
    if (error) throw error
    return (data ?? []) as CatalogItem[]
  })
}

export async function fetchCatalogProvenance(): Promise<CatalogProvenance[]> {
  return fetchAllPages(async (from, to) => {
    const { data, error } = await supabase
      .from('catalog_dimension_provenance')
      .select('*')
      .order('catalog_id')
      .range(from, to)
    if (error) throw error
    return (data ?? []) as CatalogProvenance[]
  })
}

export async function fetchCatalogItem(id: string): Promise<CatalogItem> {
  const { data, error } = await supabase
    .from('asset_catalog')
    .select('*')
    .eq('id', id)
    .single()
  if (error) throw error
  return data as CatalogItem
}

export type CatalogItemInsert = Omit<CatalogItem, 'id' | 'created_at'>

export async function createCatalogItem(item: CatalogItemInsert): Promise<CatalogItem> {
  const { data, error } = await supabase
    .from('asset_catalog')
    .insert(item)
    .select()
    .single()
  if (error) throw error
  return data as CatalogItem
}

export async function updateCatalogItem(id: string, updates: Partial<CatalogItem>): Promise<CatalogItem> {
  const { data, error } = await supabase
    .from('asset_catalog')
    .update(updates)
    .eq('id', id)
    .select()
    .single()
  if (error) throw error
  return data as CatalogItem
}

export async function deleteCatalogItem(id: string): Promise<void> {
  const { error } = await supabase
    .from('asset_catalog')
    .delete()
    .eq('id', id)
  if (error) throw error
}
